import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Stack,
  Divider,
} from '@mui/material';
import {
  TrendingUp,
  People,
  Gavel,
  AttachMoney,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import ListLoadErrorAlert from '../../components/Common/ListLoadErrorAlert';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  BarChart,
} from 'recharts';
import { getAuthToken } from '../../lib/authToken';

const COLORS = ['#1565c0', '#2e7d32', '#ed6c02', '#6a1b9a', '#00838f', '#c62828', '#5d4037', '#455a64'];

const PERIODS = [
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'this_quarter', label: 'This Quarter' },
  { value: 'this_year', label: 'This Year' },
  { value: 'last_12_months', label: 'Last 12 Months' },
];

const MetricCard: React.FC<{
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
  color?: string;
}> = ({ title, value, subtitle, icon, color = 'primary.main' }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, color }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box sx={{ color, opacity: 0.85 }}>{icon}</Box>
      </Stack>
    </CardContent>
  </Card>
);

const AnalyticsPage: React.FC = () => {
  const { user } = useAuth();
  const [period, setPeriod] = useState('this_year');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['reports', 'advanced', user?.tenantId, period],
    queryFn: async () => {
      const res = await fetch(`/api/reports/advanced?period=${period}`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      if (!res.ok) throw new Error('Failed to load advanced analytics');
      return res.json();
    },
  });

  const analytics = data?.data;
  const realization = analytics?.realization;
  const referrals = analytics?.referrals;
  const winLoss = analytics?.winLoss;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
            Advanced Analytics
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Realization rates, referral sources, and case win/loss ratios
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Period</InputLabel>
          <Select
            label="Period"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
          >
            {PERIODS.map((p) => (
              <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <ListLoadErrorAlert
          resourceLabel="analytics"
          message="Analytics couldn’t be loaded right now. Ensure you have reports access, then retry."
          onRetry={() => refetch()}
        />
      )}

      {!isLoading && analytics && (
        <Grid container spacing={3}>
          {/* KPI row */}
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Realization Rate"
              value={`${realization?.rate ?? 0}%`}
              subtitle={`$${Number(realization?.collected || 0).toLocaleString()} collected of $${Number(realization?.billed || 0).toLocaleString()} billed`}
              icon={<TrendingUp fontSize="large" />}
              color="#1565c0"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Outstanding"
              value={`$${Number(realization?.outstanding || 0).toLocaleString()}`}
              subtitle={`${realization?.invoiceCount || 0} invoices in period`}
              icon={<AttachMoney fontSize="large" />}
              color="#ed6c02"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Top Referral Source"
              value={referrals?.topSource?.name || '—'}
              subtitle={referrals?.topSource ? `${referrals.topSource.count} new clients` : 'No intake in period'}
              icon={<People fontSize="large" />}
              color="#2e7d32"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Win Rate"
              value={`${winLoss?.winRate ?? 0}%`}
              subtitle={`${winLoss?.won || 0} won · ${winLoss?.lost || 0} lost · ${winLoss?.settled || 0} settled`}
              icon={<Gavel fontSize="large" />}
              color="#6a1b9a"
            />
          </Grid>

          {/* Realization chart */}
          <Grid item xs={12} lg={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Billed vs Collected (Realization)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Collection realization = paid ÷ billed. Billable production (time): ${Number(realization?.billableProduction || 0).toLocaleString()}
                </Typography>
                {(realization?.monthly?.length || 0) === 0 ? (
                  <Alert severity="info">No invoice data for this period.</Alert>
                ) : (
                  <ResponsiveContainer width="100%" height={320}>
                    <ComposedChart data={realization.monthly}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis yAxisId="left" />
                      <YAxis yAxisId="right" orientation="right" unit="%" />
                      <RechartsTooltip />
                      <Legend />
                      <Bar yAxisId="left" dataKey="billed" name="Billed" fill="#90caf9" />
                      <Bar yAxisId="left" dataKey="collected" name="Collected" fill="#1565c0" />
                      <Line yAxisId="right" type="monotone" dataKey="rate" name="Realization %" stroke="#c62828" strokeWidth={2} />
                    </ComposedChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Realization Snapshot
                </Typography>
                <Stack spacing={2} sx={{ mt: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Billed</Typography>
                    <Typography variant="h5">${Number(realization?.billed || 0).toLocaleString()}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Collected</Typography>
                    <Typography variant="h5" color="success.main">${Number(realization?.collected || 0).toLocaleString()}</Typography>
                  </Box>
                  <Divider />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Rate</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>{realization?.rate ?? 0}%</Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Referral sources */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Referral Sources
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  New clients by intake source ({referrals?.totalClients || 0} total)
                </Typography>
                {(referrals?.bySource?.length || 0) === 0 ? (
                  <Alert severity="info">No client acquisitions with referral sources in this period.</Alert>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={referrals.bySource}
                        dataKey="count"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {referrals.bySource.map((_: any, index: number) => (
                          <Cell key={`ref-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Referrals by Volume
                </Typography>
                {(referrals?.bySource?.length || 0) === 0 ? (
                  <Alert severity="info">No referral volume to chart.</Alert>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={referrals.bySource} layout="vertical" margin={{ left: 24 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" allowDecimals={false} />
                      <YAxis type="category" dataKey="name" width={100} />
                      <RechartsTooltip />
                      <Bar dataKey="count" name="Clients" fill="#2e7d32" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Win / Loss */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Case Outcomes (Win / Loss)
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                  <Chip label={`Win rate ${winLoss?.winRate ?? 0}%`} color="primary" size="small" />
                  <Chip label={`Favorable ${(winLoss?.favorableRate ?? 0)}%`} color="success" size="small" />
                  <Chip label={`${winLoss?.totalClosed || 0} closed`} size="small" variant="outlined" />
                  {(winLoss?.missingOutcome || 0) > 0 && (
                    <Chip
                      label={`${winLoss.missingOutcome} missing outcome`}
                      color="warning"
                      size="small"
                    />
                  )}
                </Stack>
                {(winLoss?.byOutcome?.length || 0) === 0 ? (
                  <Alert severity="info">
                    No closed cases with outcomes in this period. Set an outcome when closing a case to populate this chart.
                  </Alert>
                ) : (
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={winLoss.byOutcome}
                        dataKey="count"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={95}
                        label
                      >
                        {winLoss.byOutcome.map((_: any, index: number) => (
                          <Cell key={`out-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Outcomes by Case Type
                </Typography>
                {(winLoss?.byCaseType?.length || 0) === 0 ? (
                  <Alert severity="info">No case-type outcome breakdown yet.</Alert>
                ) : (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={winLoss.byCaseType}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="type" />
                      <YAxis allowDecimals={false} />
                      <RechartsTooltip />
                      <Legend />
                      <Bar dataKey="won" stackId="a" fill="#2e7d32" name="Won" />
                      <Bar dataKey="settled" stackId="a" fill="#1565c0" name="Settled" />
                      <Bar dataKey="lost" stackId="a" fill="#c62828" name="Lost" />
                      <Bar dataKey="dismissed" stackId="a" fill="#ed6c02" name="Dismissed" />
                      <Bar dataKey="other" stackId="a" fill="#9e9e9e" name="Other" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default AnalyticsPage;
